"use client";
import { logger } from '@/lib/logger';

import React, { createContext, useContext, useCallback, useMemo } from "react";
import { useDivineParsing } from "./divine-parsing-oracle";

type Language = "en" | "es";

// Chunked Translation Context
interface ChunkedTranslationContextType {
  t: (key: string, namespace?: string) => string;
  language: Language;
  setLanguage: (lang: Language) => void;
  isLoading: boolean;
  loadedNamespaces: string[];
  loadNamespace: (namespace: string) => Promise<void>;
  loadNamespaces: (namespaces: string[]) => Promise<void>;
  preloadNamespaces: (namespaces: string[]) => void;
  isNamespaceLoaded: (namespace: string) => boolean;
  getLoadedNamespaces: () => string[];
}

const ChunkedTranslationContext = createContext<
  ChunkedTranslationContextType | undefined
>(undefined);

// Route-based namespace mapping
const ROUTE_NAMESPACES: Record<string, string[]> = {
  "/": ["common", "navigation", "hero"],
  "/features": ["common", "navigation", "features"],
  "/about": ["common", "navigation", "about"],
  "/audio": ["common", "navigation", "audio"],
  "/analysis": ["common", "navigation", "analysis"],
  "/dashboard": ["common", "navigation", "dashboard"],
};

// Get current route (simplified for client-side)
const getCurrentRoute = (): string => {
  if (typeof window === "undefined") return "/";
  return window.location.pathname;
};

// Hook for chunked translation
export function useChunkedTranslation() {
  const context = useContext(ChunkedTranslationContext);
  if (!context) {
    throw new Error("useChunkedTranslation must be used within ChunkedLanguageProvider");
  }
  return context;
}

// Chunked Language Provider Component
interface ChunkedLanguageProviderProps {
  children: React.ReactNode;
  initialNamespaces?: string[];
  autoLoadRouteNamespaces?: boolean;
}

export function ChunkedLanguageProvider({
  children,
  initialNamespaces = ["common"],
  autoLoadRouteNamespaces = true,
}: ChunkedLanguageProviderProps) {
  const {
    t: divineT,
    language,
    setLanguage,
    isLoading,
    loadedNamespaces,
    invokeOracle,
    invokeOracles,
    preinvokeOracles,
    getLoadedNamespaces,
    isOracleActive,
  } = useDivineParsing();

  // Enhanced translation function with chunked loading
  const t = useCallback(
    (key: string, namespace?: string): string => {
      // If namespace is specified and not loaded, trigger loading
      if (namespace && !isOracleActive(namespace)) {
        logger.log(`[Chunked Provider] Auto-loading namespace: ${namespace}`);
        invokeOracle(namespace).catch(error => {
          logger.warn(`[Chunked Provider] Failed to auto-load namespace: ${namespace}`, error);
        });
      }

      return divineT(key, namespace);
    },
    [divineT, isOracleActive, invokeOracle]
  );

  // Load namespace with error handling
  const loadNamespace = useCallback(
    async (namespace: string): Promise<void> => {
      try {
        await invokeOracle(namespace);
      } catch (error) {
        logger.error(`[Chunked Provider] Failed to load namespace: ${namespace}`, error);
        throw error;
      }
    },
    [invokeOracle]
  );

  // Load multiple namespaces
  const loadNamespaces = useCallback(
    async (namespaces: string[]): Promise<void> => {
      try {
        await invokeOracles(namespaces);
      } catch (error) {
        logger.error(`[Chunked Provider] Failed to load namespaces:`, namespaces, error);
        throw error;
      }
    },
    [invokeOracles]
  );

  // Preload namespaces in background
  const preloadNamespaces = useCallback(
    (namespaces: string[]): void => {
      preinvokeOracles(namespaces);
    },
    [preinvokeOracles]
  );

  // Auto-load route-based namespaces
  React.useEffect(() => {
    if (autoLoadRouteNamespaces) {
      const currentRoute = getCurrentRoute();
      const routeNamespaces = ROUTE_NAMESPACES[currentRoute] || initialNamespaces;

      // Find namespaces that aren't loaded yet
      const namespacesToLoad = routeNamespaces.filter(ns => !isOracleActive(ns));

      if (namespacesToLoad.length > 0) {
        logger.log(`[Chunked Provider] Auto-loading route namespaces:`, namespacesToLoad);
        preloadNamespaces(namespacesToLoad);
      }
    }
  }, [autoLoadRouteNamespaces, initialNamespaces, isOracleActive, preloadNamespaces]);

  // Context value
  const contextValue = useMemo(
    () => ({
      t,
      language,
      setLanguage,
      isLoading,
      loadedNamespaces,
      loadNamespace,
      loadNamespaces,
      preloadNamespaces,
      isNamespaceLoaded: isOracleActive,
      getLoadedNamespaces,
    }),
    [
      t,
      language,
      setLanguage,
      isLoading,
      loadedNamespaces,
      loadNamespace,
      loadNamespaces,
      preloadNamespaces,
      isOracleActive,
      getLoadedNamespaces,
    ]
  );

  return (
    <ChunkedTranslationContext.Provider value={contextValue}>
      {children}
    </ChunkedTranslationContext.Provider>
  );
}

// Utility hook for route-based namespace loading
export function useRouteNamespaces() {
  const { loadNamespaces, preloadNamespaces } = useChunkedTranslation();
  const currentRoute = getCurrentRoute();

  const loadRouteNamespaces = useCallback(
    async (additionalNamespaces: string[] = []) => {
      const routeNamespaces = ROUTE_NAMESPACES[currentRoute] || ["common"];
      const allNamespaces = [...new Set([...routeNamespaces, ...additionalNamespaces])];

      await loadNamespaces(allNamespaces);
    },
    [currentRoute, loadNamespaces]
  );

  const preloadRouteNamespaces = useCallback(
    (additionalNamespaces: string[] = []) => {
      const routeNamespaces = ROUTE_NAMESPACES[currentRoute] || ["common"];
      const allNamespaces = [...new Set([...routeNamespaces, ...additionalNamespaces])];

      preloadNamespaces(allNamespaces);
    },
    [currentRoute, preloadNamespaces]
  );

  return {
    loadRouteNamespaces,
    preloadRouteNamespaces,
    currentRoute,
    routeNamespaces: ROUTE_NAMESPACES[currentRoute] || ["common"],
  };
}

// Hook for conditional namespace loading
export function useConditionalNamespaces() {
  const { loadNamespace, isNamespaceLoaded } = useChunkedTranslation();

  const loadIfNeeded = useCallback(
    async (namespace: string) => {
      if (!isNamespaceLoaded(namespace)) {
        await loadNamespace(namespace);
        return true; // Was loaded
      }
      return false; // Was already loaded
    },
    [loadNamespace, isNamespaceLoaded]
  );

  const loadMultipleIfNeeded = useCallback(
    async (namespaces: string[]) => {
      const toLoad = namespaces.filter(ns => !isNamespaceLoaded(ns));

      if (toLoad.length > 0) {
        await Promise.all(toLoad.map(ns => loadNamespace(ns)));
        return toLoad; // Return what was loaded
      }

      return []; // Nothing was loaded
    },
    [loadNamespace, isNamespaceLoaded]
  );

  return {
    loadIfNeeded,
    loadMultipleIfNeeded,
  };
}

// Performance monitoring hook
export function useTranslationPerformance() {
  const { isLoading, loadedNamespaces, getLoadedNamespaces } = useChunkedTranslation();

  const getPerformanceStats = useCallback(() => {
    const loaded = getLoadedNamespaces();
    const totalNamespaces = loaded.length;

    return {
      isLoading,
      loadedNamespaces: loaded,
      totalNamespaces,
      loadingProgress: totalNamespaces > 0 ? (loaded.length / totalNamespaces) * 100 : 0,
    };
  }, [isLoading, getLoadedNamespaces]);

  return {
    getPerformanceStats,
    isLoading,
    loadedNamespaces,
  };
}